import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { GripVertical, Pencil, Trash2, Plus, X, Info } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import axios from "axios";
import { cn } from "@/lib/utils";
import { useConfirm } from "@/components/ConfirmDialogProvider";
import { FEED_SILENT_TOOLTIP } from "@/lib/kanbanConstants";

import { API_BASE_URL } from "@/lib/apiBase";
const isFeedSilent = (lane) =>
  Boolean(lane?.feed_silent ?? lane?.feedSilent);

// Sortable Lane Item Component
const SortableLaneItem = ({
  lane,
  onRename,
  onDelete,
  onToggleFeedSilent,
  isDeleting,
  isUpdatingFeedSilent,
  cardCount,
  isIntakeLane,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(lane.name);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: lane.lane_id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleSaveRename = () => {
    if (editName.trim() && editName !== lane.name) {
      onRename(lane.lane_id, editName.trim());
    }
    setIsEditing(false);
  };

  const handleCancelRename = () => {
    setEditName(lane.name);
    setIsEditing(false);
  };

  const canDelete = cardCount === 0;
  const feedSilent = isFeedSilent(lane);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex flex-col gap-2 p-3 border rounded-md bg-card",
        isDragging && "opacity-50"
      )}
    >
      <div className="flex items-center gap-2">
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
        >
          <GripVertical className="h-4 w-4" />
        </div>
        <div className="flex-1">
          {isEditing ? (
            <div className="flex items-center gap-2">
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveRename();
                  if (e.key === "Escape") handleCancelRename();
                }}
                className="h-8"
                autoFocus
              />
              <Button size="sm" variant="ghost" onClick={handleSaveRename}>
                Save
              </Button>
              <Button size="sm" variant="ghost" onClick={handleCancelRename}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="font-medium">{lane.name}</span>
              {cardCount > 0 && (
                <span className="text-xs text-muted-foreground">
                  ({cardCount} card{cardCount !== 1 ? "s" : ""})
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!isEditing && (
            <>
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8"
                onClick={() => setIsEditing(true)}
                aria-label="Rename lane"
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8"
                onClick={() => onDelete(lane.lane_id)}
                disabled={!canDelete || isDeleting}
                aria-label="Delete lane"
              >
                <Trash2 className={cn("h-4 w-4", !canDelete && "text-muted-foreground")} />
              </Button>
            </>
          )}
        </div>
      </div>

      {!isEditing && (
        <div className="flex flex-col gap-1 pl-6">
          <div className="flex items-center gap-2">
            <input
              id={`feed-silent-${lane.lane_id}`}
              type="checkbox"
              className="h-4 w-4 rounded border-input"
              checked={feedSilent || isIntakeLane}
              disabled={isUpdatingFeedSilent || isIntakeLane}
              onChange={(e) =>
                onToggleFeedSilent(lane.lane_id, e.target.checked)
              }
            />
            <Label
              htmlFor={`feed-silent-${lane.lane_id}`}
              className={cn(
                "text-sm font-normal",
                isIntakeLane
                  ? "cursor-not-allowed text-muted-foreground"
                  : "cursor-pointer"
              )}
            >
              Hide from feed
            </Label>
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex text-muted-foreground hover:text-foreground"
                    aria-label="About hide from feed"
                  >
                    <Info className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-sm leading-snug">
                  {FEED_SILENT_TOOLTIP}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          {isIntakeLane && (
            <p className="text-xs text-muted-foreground">
              Required for the team intake lane (change intake in settings to unlock).
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const KanbanLaneManager = ({
  isOpen,
  onClose,
  lanes: initialLanes,
  teamId,
  orgId,
  accessToken,
  onLanesUpdated,
  posts = [],
  intakeLaneId = null,
}) => {
  const confirm = useConfirm();
  // Initialize state with proper array handling
  const [lanes, setLanes] = useState(() => {
    // Ensure initialLanes is an array before using
    if (Array.isArray(initialLanes)) {
      // Filter lanes to only include those for the current team (defensive check)
      return initialLanes.filter(lane => {
        // If team_id is not present in lane object, assume it's valid (backend should filter)
        // If team_id is present, verify it matches current teamId
        return !lane.team_id || lane.team_id === parseInt(teamId);
      });
    }
    return [];
  });
  const [newLaneName, setNewLaneName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [, setIsRenaming] = useState(false);
  const [isReordering, setIsReordering] = useState(false);
  const [error, setError] = useState(null);
  const [deletingLaneId, setDeletingLaneId] = useState(null);
  const [updatingFeedSilentLaneId, setUpdatingFeedSilentLaneId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Update lanes when initialLanes prop changes, and filter to ensure they belong to current team
  React.useEffect(() => {
    // Handle both undefined and array cases
    if (initialLanes !== undefined) {
      // Ensure initialLanes is an array
      if (Array.isArray(initialLanes)) {
        // Filter lanes to only include those for the current team (defensive check)
        // Only filter if team_id exists AND doesn't match - don't filter out lanes without team_id
        const filteredLanes = initialLanes.filter(lane => {
          // If team_id is not present in lane object, assume it's valid (backend should filter)
          // If team_id is present, verify it matches current teamId
          return !lane.team_id || lane.team_id === parseInt(teamId);
        });
        setLanes(filteredLanes);
      } else {
        // If initialLanes is not an array, set empty array
        setLanes([]);
      }
    }
  }, [initialLanes, teamId]);

  // Count cards per lane
  const getCardCountForLane = (laneName) => {
    return posts.filter(
      (post) => (post.kanbanStatus || post.kanban_status) === laneName
    ).length;
  };

  const handleCreateLane = async () => {
    if (!newLaneName.trim()) {
      setError("Lane name cannot be empty");
      return;
    }

    // Check for duplicate names
    if (lanes.some((lane) => lane.name.toLowerCase() === newLaneName.trim().toLowerCase())) {
      setError("A lane with this name already exists");
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/team/${teamId}/kanban-lanes`,
        {
          org_id: orgId,
          team_id: parseInt(teamId),
          name: newLaneName.trim(),
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      console.log("Lane creation response:", response.data);

      // Handle different response structures - backend might return lane directly or wrapped
      let newLane = response.data;
      if (response.data && typeof response.data === 'object' && !response.data.lane_id) {
        // If response is wrapped, try to extract the lane
        newLane = response.data.data || response.data.lane || response.data;
      }

      if (newLane && newLane.lane_id) {
        // Add new lane to local state and sort by position
        const updatedLanes = [...lanes, newLane].sort((a, b) => (a.position || 0) - (b.position || 0));
        setLanes(updatedLanes);
        console.log("Updated lanes after creation:", updatedLanes);
      } else {
        console.warn("Created lane response missing lane_id, refreshing from server");
      }
      
      setNewLaneName("");
      
      // Always call onLanesUpdated to refresh parent state
      if (onLanesUpdated) {
        await onLanesUpdated();
      }
    } catch (err) {
      console.error("Failed to create lane:", err);
      
      // Map technical error messages to user-friendly ones
      let errorMessage = "Failed to create lane";
      const errorResponse = err.response?.data;
      
      if (errorResponse?.message) {
        const message = errorResponse.message;
        
        if (message.includes("already exists")) {
          errorMessage = message; // Keep the duplicate name message as is
        } else if (message.includes("Only team admins")) {
          errorMessage = "You don't have permission to create lanes. Only team admins can manage lanes.";
        } else if (message.includes("not found")) {
          errorMessage = "Team not found. Please refresh the page and try again.";
        } else {
          errorMessage = "Unable to create lane. Please try again.";
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  const handleRenameLane = async (laneId, newName) => {
    if (!newName.trim()) {
      setError("Lane name cannot be empty");
      return;
    }

    // Validate that the lane exists in the current lanes array
    const lane = lanes.find((l) => l.lane_id === laneId);
    if (!lane) {
      setError("This lane no longer exists. Please refresh the page and try again.");
      return;
    }

    // Check for duplicate names
    if (lanes.some((lane) => lane.lane_id !== laneId && lane.name.toLowerCase() === newName.trim().toLowerCase())) {
      setError("A lane with this name already exists");
      return;
    }

    setIsRenaming(true);
    setError(null);

    try {
      const response = await axios.patch(
        `${API_BASE_URL}/team/${teamId}/kanban-lanes/${laneId}`,
        {
          org_id: orgId,
          team_id: parseInt(teamId),
          lane_id: laneId,
          name: newName.trim(),
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      // Update local state with the response data if available, otherwise use optimistic update
      const updatedLane = response.data?.data || response.data;
      if (updatedLane) {
        setLanes(lanes.map((lane) => 
          lane.lane_id === laneId ? { ...lane, name: updatedLane.name || newName.trim() } : lane
        ));
      } else {
        setLanes(lanes.map((lane) => 
          lane.lane_id === laneId ? { ...lane, name: newName.trim() } : lane
        ));
      }
      
      if (onLanesUpdated) {
        await onLanesUpdated();
      }
    } catch (err) {
      console.error("Failed to rename lane:", err);
      
      // Map technical error messages to user-friendly ones
      let errorMessage = "Failed to rename lane";
      const errorResponse = err.response?.data;
      
      if (errorResponse?.message) {
        const message = errorResponse.message;
        
        if (message.includes("does not belong to this team") || message.includes("not found for team")) {
          errorMessage = "This lane no longer exists or belongs to a different team. Please refresh the page and try again.";
        } else if (message.includes("does not exist")) {
          errorMessage = "This lane no longer exists. Please refresh the page and try again.";
        } else if (message.includes("already exists")) {
          errorMessage = message; // Keep the duplicate name message as is
        } else if (message.includes("Only team admins")) {
          errorMessage = "You don't have permission to rename lanes. Only team admins can manage lanes.";
        } else {
          errorMessage = "Unable to rename lane. Please refresh the page and try again.";
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsRenaming(false);
    }
  };

  const handleUpdateFeedSilent = async (laneId, feedSilent) => {
    const lane = lanes.find((l) => l.lane_id === laneId);
    if (!lane) {
      setError("This lane no longer exists. Please refresh the page and try again.");
      return;
    }

    setUpdatingFeedSilentLaneId(laneId);
    setError(null);

    try {
      const response = await axios.patch(
        `${API_BASE_URL}/team/${teamId}/kanban-lanes/${laneId}`,
        {
          org_id: orgId,
          team_id: parseInt(teamId),
          lane_id: laneId,
          feed_silent: feedSilent,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const updatedLane = response.data?.data || response.data;
      setLanes(
        lanes.map((l) =>
          l.lane_id === laneId
            ? {
                ...l,
                feed_silent:
                  updatedLane?.feed_silent ??
                  updatedLane?.feedSilent ??
                  feedSilent,
              }
            : l
        )
      );

      if (onLanesUpdated) {
        await onLanesUpdated();
      }
    } catch (err) {
      console.error("Failed to update feed silent setting:", err);
      let errorMessage = "Failed to update hide-from-feed setting";
      const message = err.response?.data?.message;
      if (message?.includes("Only team admins")) {
        errorMessage =
          "You don't have permission to update lanes. Only team admins can manage lanes.";
      } else if (message?.includes("does not exist")) {
        errorMessage =
          "This lane no longer exists. Please refresh the page and try again.";
      }
      setError(errorMessage);
    } finally {
      setUpdatingFeedSilentLaneId(null);
    }
  };

  const handleDeleteLane = async (laneId) => {
    const lane = lanes.find((l) => l.lane_id === laneId);
    if (!lane) return;

    const cardCount = getCardCountForLane(lane.name);
    if (cardCount > 0) {
      setError(`Cannot delete lane "${lane.name}" because it contains ${cardCount} card(s). Please move or delete all cards first.`);
      return;
    }

    const ok = await confirm({
      title: "Delete lane?",
      description: `Are you sure you want to delete the lane "${lane.name}"?`,
      confirmLabel: "Delete",
      variant: "destructive",
    });
    if (!ok) return;

    setDeletingLaneId(laneId);
    setIsDeleting(true);
    setError(null);

    try {
      await axios.delete(
        `${API_BASE_URL}/team/${teamId}/kanban-lanes/${laneId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            org_id: orgId,
            team_id: parseInt(teamId),
            lane_id: laneId,
          },
        }
      );

      // Remove lane from local state
      setLanes(lanes.filter((lane) => lane.lane_id !== laneId));
      
      if (onLanesUpdated) {
        await onLanesUpdated();
      }
    } catch (err) {
      console.error("Failed to delete lane:", err);
      
      // Map technical error messages to user-friendly ones
      let errorMessage = "Failed to delete lane";
      const errorResponse = err.response?.data;
      
      if (errorResponse?.message) {
        const message = errorResponse.message;
        
        if (message.includes("does not belong to this team") || message.includes("not found for team")) {
          errorMessage = "This lane no longer exists or belongs to a different team. Please refresh the page and try again.";
        } else if (message.includes("does not exist")) {
          errorMessage = "This lane no longer exists. Please refresh the page and try again.";
        } else if (message.includes("Only team admins")) {
          errorMessage = "You don't have permission to delete lanes. Only team admins can manage lanes.";
        } else {
          errorMessage = "Unable to delete lane. Please refresh the page and try again.";
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsDeleting(false);
      setDeletingLaneId(null);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = lanes.findIndex((lane) => lane.lane_id === active.id);
    const newIndex = lanes.findIndex((lane) => lane.lane_id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Log lanes before reorder to debug
    console.log("Lanes before reorder:", lanes);
    console.log("Old index:", oldIndex, "New index:", newIndex);
    
    // Update local state optimistically
    const reorderedLanes = arrayMove(lanes, oldIndex, newIndex);
    console.log("Reordered lanes:", reorderedLanes);
    
    const updatedLanes = reorderedLanes.map((lane, index) => {
      // Ensure lane_id is preserved - check both snake_case and camelCase
      const laneId = lane.lane_id || lane.laneId;
      if (!laneId) {
        console.error("Lane missing lane_id in reorder:", lane);
        throw new Error(`Lane at index ${index} is missing lane_id: ${JSON.stringify(lane)}`);
      }
      return {
        ...lane,
        lane_id: laneId, // Ensure lane_id is set
        position: index,
      };
    });
    console.log("Updated lanes after reorder:", updatedLanes);
    setLanes(updatedLanes);

    // Save to backend
    setIsReordering(true);
    setError(null);

    try {
      // Ensure all lanes have valid lane_id and position
      const lanesData = updatedLanes.map((lane, index) => {
        const laneId = lane.lane_id || lane.laneId;
        const position = lane.position !== undefined ? lane.position : index;
        
        if (laneId === undefined || laneId === null || laneId === '') {
          console.error("Lane missing lane_id:", lane);
          throw new Error(`Lane at index ${index} is missing lane_id: ${JSON.stringify(lane)}`);
        }
        
        const laneIdNum = typeof laneId === 'number' ? laneId : parseInt(laneId, 10);
        const positionNum = typeof position === 'number' ? position : parseInt(position, 10);
        
        if (isNaN(laneIdNum) || isNaN(positionNum)) {
          console.error("Invalid lane data:", { laneId, position, laneIdNum, positionNum });
          throw new Error(`Invalid lane data at index ${index}: lane_id=${laneId}, position=${position}`);
        }
        
        return {
          lane_id: laneIdNum,
          position: positionNum,
        };
      });
      
      console.log("Processed lanes data:", lanesData);
      
      // Verify payload structure and types
      lanesData.forEach((lane, index) => {
        console.log(`Lane ${index}:`, {
          lane_id: lane.lane_id,
          lane_id_type: typeof lane.lane_id,
          position: lane.position,
          position_type: typeof lane.position,
        });
        if (typeof lane.lane_id !== 'number' || typeof lane.position !== 'number') {
          console.error(`Invalid types at index ${index}:`, lane);
        }
      });
      
      const requestData = {
        org_id: orgId,
        team_id: parseInt(teamId),
        lanes: lanesData,
      };
      
      console.log("Reordering lanes with data:", requestData);
      console.log("Lanes array details:", lanesData);
      console.log("Request data JSON:", JSON.stringify(requestData));
      
      const response = await axios.patch(
        `${API_BASE_URL}/team/${teamId}/kanban-lanes/reorder`,
        requestData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      
      console.log("Reorder response:", response.data);

      if (onLanesUpdated) {
        await onLanesUpdated();
      }
    } catch (err) {
      console.error("Failed to reorder lanes:", err);
      console.error("Error response data:", err.response?.data);
      console.error("Error response status:", err.response?.status);
      
      // Log the full error message array
      if (err.response?.data?.message && Array.isArray(err.response.data.message)) {
        console.error("Error messages:", err.response.data.message);
      }
      
      // Map technical error messages to user-friendly ones
      let errorMessage = "Failed to reorder lanes";
      const errorResponse = err.response?.data;
      
      if (errorResponse?.message) {
        const message = Array.isArray(errorResponse.message) 
          ? errorResponse.message.join(", ")
          : errorResponse.message;
        
        console.error("Full error message:", message);
        
        if (message.includes("should not be empty") || message.includes("must be a number")) {
          errorMessage = "Invalid lane data. Please refresh the page and try again.";
        } else if (message.includes("Only team admins")) {
          errorMessage = "You don't have permission to reorder lanes. Only team admins can manage lanes.";
        } else {
          errorMessage = message;
        }
      }
      
      setError(errorMessage);
      // Revert to original order on error
      setLanes(initialLanes || []);
    } finally {
      setIsReordering(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage Kanban Lanes</DialogTitle>
          <DialogDescription>
            Add, rename, delete, and reorder kanban lanes for this team. Use
            &quot;Hide from feed&quot; to keep shaping work off organization feeds.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4 py-4">
          {/* Add New Lane */}
          <div className="flex items-center gap-2">
            <Input
              placeholder="New lane name"
              value={newLaneName}
              onChange={(e) => {
                setNewLaneName(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleCreateLane();
                }
              }}
              disabled={isCreating}
            />
            <Button
              onClick={handleCreateLane}
              disabled={isCreating || !newLaneName.trim()}
              size="sm"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Lane
            </Button>
          </div>

          {/* Lanes List */}
          <div className="space-y-2">
            <Label>Lanes (drag to reorder)</Label>
            {lanes.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No lanes configured. Add a lane to get started.
              </p>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={lanes.map((lane) => lane.lane_id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {lanes
                      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
                      .map((lane) => (
                        <SortableLaneItem
                          key={lane.lane_id}
                          lane={lane}
                          onRename={handleRenameLane}
                          onDelete={handleDeleteLane}
                          onToggleFeedSilent={handleUpdateFeedSilent}
                          isDeleting={isDeleting && deletingLaneId === lane.lane_id}
                          isUpdatingFeedSilent={
                            updatingFeedSilentLaneId === lane.lane_id
                          }
                          cardCount={getCardCountForLane(lane.name)}
                          isIntakeLane={
                            intakeLaneId != null &&
                            lane.lane_id === intakeLaneId
                          }
                        />
                      ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isReordering}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default KanbanLaneManager;

